import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import CategoryManagement from './CategoryManagement';
import MenuManagement from './MenuManagement';
import TableManagement from './TableManagement';
import OrderManagement from './OrderManagement';
import RestaurantProfile from './RestaurantProfile';
import TaxManagement from './TaxManagement';
import ReceiptCustomization from './ReceiptCustomization';
import Analytics from './Analytics';
import RestaurantOrderCreation from './RestaurantOrderCreation';
import DailyReports from './DailyReports';
import StripePaymentModal from './StripePaymentModal';

const RestaurantDashboard = ({ apiClient, userId, onLogout }) => {
  const [activeTab, setActiveTab] = useState('orders');
  const [activeOrderSubTab, setActiveOrderSubTab] = useState('order-management');
  const [activeProfileSubTab, setActiveProfileSubTab] = useState('restaurant-info');
  const [restaurant, setRestaurant] = useState(null);
  const [orderCapacity, setOrderCapacity] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasProfilePassword, setHasProfilePassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [profilePassword, setProfilePassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [purchaseModal, setPurchaseModal] = useState({ show: false, quantity: 1, pricing: null });
  const [paymentModal, setPaymentModal] = useState({ 
    show: false, 
    clientSecret: null, 
    orderDetails: null 
  });

  useEffect(() => {
    fetchRestaurantData();
    checkProfilePasswordStatus();
  }, []);

  const checkProfilePasswordStatus = async () => {
    try {
      const response = await apiClient.get('/restaurant/profile/password-status');
      setHasProfilePassword(response.data.has_profile_password);
    } catch (error) {
      console.error('Error checking password status:', error);
    }
  };

  const handleTabClick = (tabId) => {
    // Show password modal for Profile tab
    if (tabId === 'profile') {
      if (!hasProfilePassword) {
        toast.error('Please set your profile password before accessing Profile settings');
        return;
      }
      // Show password prompt modal
      setShowPasswordModal(true);
      return;
    }
    setActiveTab(tabId);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    
    try {
      // Verify profile password
      await apiClient.post('/restaurant/profile/verify-password', {
        password: profilePassword
      });
      
      // Password correct - allow access to Profile tab
      setShowPasswordModal(false);
      setProfilePassword('');
      setActiveTab('profile');
      toast.success('Access granted');
    } catch (error) {
      console.error('Error verifying password:', error);
      setPasswordError(error.response?.data?.detail || 'Incorrect password');
    }
  };

  const handlePasswordModalClose = () => {
    setShowPasswordModal(false);
    setProfilePassword('');
    setPasswordError('');
  };

  const handleSendPasswordSetup = async () => {
    try {
      await apiClient.post('/restaurant/profile/send-password-setup');
      toast.success('Password setup link sent to your email');
    } catch (error) {
      console.error('Error sending password setup link:', error);
      toast.error(error.response?.data?.detail || 'Failed to send password setup link');
    }
  };

  const fetchRestaurantData = async () => {
    try {
      const [profileResponse, capacityResponse] = await Promise.all([
        apiClient.get('/restaurant/profile'),
        apiClient.get('/restaurant/order-capacity')
      ]);
      
      setRestaurant(profileResponse.data);
      setOrderCapacity(capacityResponse.data.order_capacity);
    } catch (error) {
      console.error('Error fetching restaurant data:', error);
      toast.error('Failed to load restaurant data');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseCapacity = async () => {
    try {
      // Fetch pricing information
      const pricingResponse = await apiClient.get('/restaurant/capacity-pricing');
      setPurchaseModal({ 
        show: true, 
        quantity: 1, 
        pricing: pricingResponse.data 
      });
    } catch (error) {
      console.error('Error fetching pricing:', error);
      toast.error('Failed to load pricing information');
    }
  };

  const handleConfirmPurchase = async () => {
    try {
      const response = await apiClient.post('/restaurant/purchase-capacity', {
        quantity: purchaseModal.quantity
      });
      
      if (response.data.requires_payment) {
        // Show Stripe payment modal
        setPaymentModal({
          show: true,
          clientSecret: response.data.client_secret,
          orderDetails: {
            packages: response.data.packages,
            orders: response.data.orders_to_add,
            amount: response.data.amount
          }
        });
        setPurchaseModal({ show: false, quantity: 1, pricing: null });
      } else {
        // Simulated purchase completed
        toast.success(response.data.message);
        setPurchaseModal({ show: false, quantity: 1, pricing: null });
        fetchRestaurantData(); // Refresh data
      }
    } catch (error) {
      console.error('Error purchasing capacity:', error);
      toast.error(error.response?.data?.detail || 'Failed to purchase order capacity');
    }
  };

  const handlePaymentSuccess = async (paymentIntentId) => {
    try {
      const response = await apiClient.post('/restaurant/confirm-capacity-payment', {
        payment_intent_id: paymentIntentId
      });
      
      toast.success(response.data.message);
      setPaymentModal({ show: false, clientSecret: null, orderDetails: null });
      fetchRestaurantData(); // Refresh data
    } catch (error) {
      console.error('Error confirming payment:', error);
      toast.error('Failed to confirm payment');
    }
  };

  const calculateTotalPrice = () => {
    if (!purchaseModal.pricing) return 0;
    return purchaseModal.pricing.price_per_package * purchaseModal.quantity;
  };

  const calculateTotalOrders = () => {
    if (!purchaseModal.pricing) return 0;
    return purchaseModal.pricing.orders_per_package * purchaseModal.quantity;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block">
            <div className="spinner mx-auto mb-4" style={{ width: '48px', height: '48px', borderWidth: '4px' }}></div>
          </div>
          <p className="text-lg text-gray-700 font-medium">Loading dashboard...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait while we fetch your data</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'profile', name: 'Profile', icon: '👤' },
    { id: 'orders', name: 'Orders', icon: '📋' }
  ];

  const orderSubTabs = [
    { id: 'order-management', name: 'Order Management', icon: '📋' },
    { id: 'create-order', name: 'Create Order', icon: '➕' }
  ];

  const profileSubTabs = [
    { id: 'restaurant-info', name: 'Restaurant Info', icon: '🏪' },
    { id: 'categories', name: 'Categories', icon: '📂' },
    { id: 'menu', name: 'Menu Items', icon: '🍽️' },
    { id: 'tables', name: 'Tables & QR', icon: '📱' },
    { id: 'analytics', name: 'Analytics', icon: '📊' },
    { id: 'taxes', name: 'Bill Settings', icon: '💰' },
    { id: 'daily-reports', name: 'Daily Reports', icon: '📄' },
    { id: 'receipts', name: 'Receipt Config', icon: '🧾' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile: 2-row layout with centered name */}
          <div className="sm:hidden py-4">
            {/* Row 1: Restaurant name centered */}
            <div className="flex justify-center mb-3">
              <h1 className="text-xl font-semibold text-gray-900" data-testid="restaurant-name">
                {restaurant?.name}
              </h1>
            </div>
            
            {/* Row 2: Capacity info and logout */}
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">Capacity:</span>
                <span 
                  className={`px-2 py-1 rounded-full text-sm font-medium ${
                    orderCapacity > 10 
                      ? 'bg-green-100 text-green-800' 
                      : orderCapacity > 0 
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                  }`}
                  data-testid="order-capacity"
                >
                  {orderCapacity}
                </span>
                <button
                  onClick={handlePurchaseCapacity}
                  data-testid="purchase-capacity-button"
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap"
                >
                  Purchase Capacity
                </button>
              </div>
              
              <button
                onClick={onLogout}
                data-testid="logout-button"
                className="text-gray-500 hover:text-gray-700 text-sm font-medium"
              >
                Logout
              </button>
            </div>
            
            {/* Row 3: Password button */}
            <div className="flex justify-center">
              <button
                onClick={handleSendPasswordSetup}
                className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                {hasProfilePassword ? '🔑 Forgot Profile Password' : '🔒 Set Profile Password'}
              </button>
            </div>
          </div>
          
          {/* Desktop: Single row layout */}
          <div className="hidden sm:flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-semibold text-gray-900" data-testid="restaurant-name">
                {restaurant?.name}
              </h1>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">Order Capacity:</span>
                <span 
                  className={`px-2 py-1 rounded-full text-sm font-medium ${
                    orderCapacity > 10 
                      ? 'bg-green-100 text-green-800' 
                      : orderCapacity > 0 
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                  }`}
                  data-testid="order-capacity"
                >
                  {orderCapacity}
                </span>
                <button
                  onClick={handlePurchaseCapacity}
                  data-testid="purchase-capacity-button"
                  className="ml-2 px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Purchase Capacity
                </button>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSendPasswordSetup}
                className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center"
              >
                {hasProfilePassword ? '🔑 Forgot Profile Password' : '🔒 Set Profile Password'}
              </button>
              
              <button
                onClick={onLogout}
                data-testid="logout-button"
                className="text-gray-500 hover:text-gray-700 text-sm font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex flex-wrap gap-x-8 gap-y-2 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  data-testid={`${tab.id}-tab`}
                  className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap flex items-center ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="fade-in">
          {activeTab === 'orders' && (
            <div className="space-y-6">
              {/* Orders Sub-tabs */}
              <div className="bg-white rounded-lg shadow-sm">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex flex-wrap gap-x-6 gap-y-2 px-6">
                    {orderSubTabs.map((subTab) => (
                      <button
                        key={subTab.id}
                        onClick={() => setActiveOrderSubTab(subTab.id)}
                        data-testid={`${subTab.id}-subtab`}
                        className={`py-3 px-2 border-b-2 font-medium text-sm whitespace-nowrap flex items-center ${
                          activeOrderSubTab === subTab.id
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <span className="mr-2">{subTab.icon}</span>
                        {subTab.name}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              {/* Orders Sub-tab Content */}
              {activeOrderSubTab === 'order-management' && (
                <OrderManagement apiClient={apiClient} onPurchaseCapacity={handlePurchaseCapacity} />
              )}
              {activeOrderSubTab === 'create-order' && (
                <RestaurantOrderCreation apiClient={apiClient} />
              )}
            </div>
          )}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              {/* Profile Sub-tabs */}
              <div className="bg-white rounded-lg shadow-sm">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex flex-wrap gap-x-6 gap-y-2 px-6">
                    {profileSubTabs.map((subTab) => (
                      <button
                        key={subTab.id}
                        onClick={() => setActiveProfileSubTab(subTab.id)}
                        data-testid={`${subTab.id}-subtab`}
                        className={`py-3 px-2 border-b-2 font-medium text-sm whitespace-nowrap flex items-center ${
                          activeProfileSubTab === subTab.id
                            ? 'border-purple-500 text-purple-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <span className="mr-2">{subTab.icon}</span>
                        {subTab.name}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              {/* Profile Sub-tab Content */}
              {activeProfileSubTab === 'restaurant-info' && (
                <RestaurantProfile 
                  apiClient={apiClient} 
                  restaurant={restaurant} 
                  onUpdate={fetchRestaurantData}
                  onPurchaseCapacity={handlePurchaseCapacity}
                />
              )}
              {activeProfileSubTab === 'categories' && (
                <CategoryManagement apiClient={apiClient} />
              )}
              {activeProfileSubTab === 'menu' && (
                <MenuManagement apiClient={apiClient} />
              )}
              {activeProfileSubTab === 'tables' && (
                <TableManagement apiClient={apiClient} />
              )}
              {activeProfileSubTab === 'analytics' && (
                <Analytics apiClient={apiClient} />
              )}
              {activeProfileSubTab === 'taxes' && (
                <TaxManagement apiClient={apiClient} />
              )}
              {activeProfileSubTab === 'daily-reports' && (
                <DailyReports apiClient={apiClient} />
              )}
              {activeProfileSubTab === 'receipts' && (
                <ReceiptCustomization apiClient={apiClient} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Order Capacity Purchase Modal */}
      {purchaseModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Purchase Order Capacity</h3>
            
            {purchaseModal.pricing && (
              <div className="space-y-4">
                {/* Current Status */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">
                    Current Capacity: <span className="font-semibold">{orderCapacity} orders</span>
                  </p>
                  {orderCapacity > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      💡 Stock up on order capacity for busy periods or special events
                    </p>
                  )}
                </div>

                {/* Package Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Packages (50 orders each)
                  </label>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => setPurchaseModal(prev => ({ 
                        ...prev, 
                        quantity: Math.max(1, prev.quantity - 1) 
                      }))}
                      className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      -
                    </button>
                    <span className="w-12 text-center font-medium">{purchaseModal.quantity}</span>
                    <button
                      onClick={() => setPurchaseModal(prev => ({ 
                        ...prev, 
                        quantity: Math.min(20, prev.quantity + 1) 
                      }))}
                      className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Quick Select Buttons */}
                <div>
                  <p className="text-sm text-gray-700 mb-2">Quick Select:</p>
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 3, 5, 10].map(qty => (
                      <button
                        key={qty}
                        onClick={() => setPurchaseModal(prev => ({ ...prev, quantity: qty }))}
                        className={`px-3 py-1 text-sm rounded-md border ${
                          purchaseModal.quantity === qty
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {qty} pkg{qty > 1 ? 's' : ''}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Purchase Summary */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Orders to add:</span>
                      <span className="font-medium">{calculateTotalOrders()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Price per package:</span>
                      <span>{purchaseModal.pricing.price_per_package} USD</span>
                    </div>
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-medium">Total Cost:</span>
                      <span className="font-bold text-lg">{calculateTotalPrice()} USD</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>New total capacity:</span>
                      <span>{orderCapacity + calculateTotalOrders()} orders</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    onClick={() => setPurchaseModal({ show: false, quantity: 1, pricing: null })}
                    className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmPurchase}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Purchase ${calculateTotalPrice()}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stripe Payment Modal */}
      <StripePaymentModal
        isOpen={paymentModal.show}
        onClose={() => setPaymentModal({ show: false, clientSecret: null, orderDetails: null })}
        clientSecret={paymentModal.clientSecret}
        orderDetails={paymentModal.orderDetails}
        onPaymentSuccess={handlePaymentSuccess}
        apiClient={apiClient}
      />

      {/* Profile Password Verification Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Enter Profile Password</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please enter your profile password to access Profile settings.
            </p>
            
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label htmlFor="profile-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Profile Password
                </label>
                <input
                  type="password"
                  id="profile-password"
                  value={profilePassword}
                  onChange={(e) => setProfilePassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your profile password"
                  autoFocus
                  required
                />
                {passwordError && (
                  <p className="mt-1 text-sm text-red-600">{passwordError}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handlePasswordModalClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Verify
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantDashboard;